import { Browser } from "../../core/browser";
import { ChipsideComponent } from "../chipside-component";

export class Input extends ChipsideComponent {
	static observedAttributes = ["value", "placeholder", "icon-style", "type", "suffix-button", "suffix-tooltip", "readonly", "variation", "helper-text"];

	#Mappers = {};

	#GetSearchedItems = null;
	#InputTimeout;
	#Dropdown = null;

	constructor() {
		super();

		this.identifier = this.getAttribute("identifier") ?? crypto.randomUUID().slice(0, 8);
		this.label = this.getAttribute("label");
		this.disableMessages = this.hasAttribute("disable-messages");
		this.secondaryLabel = this.getAttribute("secondary-label") ?? "";
		this.showPasswordButton = this.hasAttribute("show-password-button");
		this.suffixIcon = this.getAttribute("suffix-icon") ?? "";
		this.prefixIcon = this.getAttribute("prefix-icon") ?? "";
		this.orientation = this.getAttribute("orientation");
		this.iconStyle = this.getAttribute("icon-style") ?? "";
		this.variation = this.getAttribute("variation") ?? "";
		this.readonly = this.hasAttribute("readonly");
		this.labelTooltip = this.getAttribute("label-tooltip");
		this.helperText = this.getAttribute("helper-text") ?? "";
		this.casing = this.getAttribute("casing") ?? "";
		this.type = this.getAttribute("type") ?? "text";
		this.size = this.getAttribute("size");
		this.prefixButton = this.getAttribute("prefix-button") ?? "";
		this.suffixButton = this.getAttribute("suffix-button") ?? "";
		this.prefixTooltip = this.getAttribute("prefix-tooltip") ?? "";
		this.suffixTooltip = this.getAttribute("suffix-tooltip") ?? "";
		this.route = this.getAttribute("route") ?? "";
		this.searchParameter = this.getAttribute("search-parameter") ?? "q";

		if (this.type === "password" && this.showPasswordButton) {
			this.suffixButton = "fas fa-eye";
			this.suffixTooltip = "Show password";

			this.addEventListener("suffixclick", () => {
				if (this.type === "password") {
					this.suffixButton = "fas fa-eye-slash";
					this.type = "text";
					this.suffixTooltip = "Hide password";
				} else {
					this.suffixButton = "fas fa-eye";
					this.type = "password";
					this.suffixTooltip = "Show password";
				}
			});
		}

		if (this.type === "search" && !this.prefixIcon && !this.prefixButton) {
			this.prefixIcon = "fal fa-search";
			this.iconStyle = "integrated";
		}

		this.value = (() => {
			switch (this.casing.toLowerCase()) {
				case "uppercase": return this.getAttribute("value")?.toUpperCase();
				case "lowercase": return this.getAttribute("value")?.toLowerCase();
				default: return this.getAttribute("value")
			}
		})() ?? "";

		this.initialValue = this.value;

		this.#Mappers = {
			list: this.getAttribute("list"),
			max: this.getAttribute("max"),
			maxlength: this.getAttribute("maxlength"),
			min: this.getAttribute("min"),
			minlength: this.getAttribute("minlength"),
			name: this.getAttribute("name"),
			autofocus: this.hasAttribute("focus"),
			autocomplete: this.getAttribute("autocomplete"),
			pattern: this.getAttribute("pattern"),
			placeholder: this.getAttribute("placeholder"),
			readonly: this.readonly,
			required: this.hasAttribute("required"),
			step: this.getAttribute("step"),
			tabIndex: this.getAttribute("tabindex") ?? ""
		};

		if (this.closest("form") && !this.#Mappers.name) {
			this.#Mappers.name = this.id ?? this.identifier;
		}

		if (this.type.toLowerCase() === "date" && !this.#Mappers.max) {
			this.#Mappers.max = "9999-12-31";
		}

		Object.keys(this.#Mappers).forEach(key => this[key] = this.#Mappers[key]);
	}

	get GetSearchedItems() {
		return this.#GetSearchedItems;
	}

	set GetSearchedItems(value) {
		this.#GetSearchedItems = value;

		const dropdown = this.querySelector("chip-dropdown");

		if (dropdown) {
			dropdown.GetSearchedItems = value;
		}
	}

	#OnInput(input) {
		// Other types will error when setting these values
		if (["text", "search", "url", "tel", "password"].includes(this.type.toLowerCase())) {
			const trueValue = (() => {
				switch (this.casing.toLowerCase()) {
					case "uppercase": return input.value.toUpperCase();
					case "lowercase": return input.value.toLowerCase();
					default: return input.value
				}
			})() ?? "";

			Object.assign(input, {
				value: trueValue,
				selectionEnd: input.selectionStart
			});

			this.value = trueValue;
		} else {
			this.value = input.value;
		}

		if (this.#Dropdown) {
			input.addEventListener("keydown", ev => {
				if (ev.key === "ArrowUp") {
					this.#Dropdown.ArrowUp(ev);
				} else if (ev.key === "ArrowDown") {
					this.#Dropdown.ArrowDown(ev);
				}
			});

			if (this.#InputTimeout) {
				clearTimeout(this.#InputTimeout);
			}

			this.#InputTimeout = setTimeout(() => {
				this.#Dropdown?.Search(input.value).then(() => {
					if (!this.#Dropdown.querySelector("chip-dropdownitem") || input.value.trim() === "") {
						this.#Dropdown.Close();
					} else {
						this.#Dropdown.Open();
					}
				});
			}, 500);
		}
	}

	parsedCallback() {
		this.removeAttribute("autofocus");
		this.removeAttribute("tabindex");

		const element = document.createElementWithContents("div",
			`
				<div class="input__wrapper">
					<input class="input-field"/>
				</div>
			`.trim()
		),
			input = element.querySelector("input");

		Object.assign(input, {
			type: this.type,
			value: this.value,
			id: this.identifier
		});

		if (this.helperText) { element.appendChild(document.createElementWithContents("div", this.helperText, { className: "input-field__helper-text" })); }

		this.#Dropdown = this.querySelector(":scope>chip-dropdown");

		const label = document.createElementWithContents("label", this.label, {
			className: "input-field__label",
			htmlFor: this.identifier
		});

		if (this.labelTooltip) {
			const labelIcon = document.createElementWithContents("i", null, { className: "fas fa-info-circle" });
			labelIcon.setAttribute("tooltip", this.labelTooltip);

			label.appendChild(labelIcon);
		}

		if (this.secondaryLabel) { label.appendChild(document.createElementWithContents("span", this.secondaryLabel, { className: "input-field__label--secondary" })); }

		if (!this.orientation) {
			element.prepend(label);
		} else {
			element.querySelector(".input__wrapper").prepend(label);
		}

		input.onchange = ev => {
			ev.preventDefault();
			ev.stopPropagation();

			this.reportValidity();
			this.closest("chip-form")?.checkValidity();

			this.raiseEvent("change");
		}

		input.addEventListener("input", () => this.#OnInput(input));

		if (this.type === "search") {
			input.addEventListener("keyup", ev => {
				if (ev.key === "Enter") {
					if (this.route) {
						const url = Object.assign(new URL(Browser.ResolveRelativeURL(this.route)), {
							hash: ""
						});

						url.searchParams.set(this.searchParameter, input.value.trim() === "" ? null : input.value.trim());

						location.href = url;
					} else {
						Browser.SetURLParameter(this.searchParameter, input.value.trim() === "" ? null : input.value.trim());
					}
				}
			});
		}

		if (this.#Dropdown) {
			if (this.GetSearchedItems) {
				this.#Dropdown.GetSearchedItems = this.GetSearchedItems;
			}

			this.#Dropdown.onchange = () => {
				input.value = this.#Dropdown.value ?? this.#Dropdown.selectedText;
			}

			input.addEventListener("focus", () => {
				if (input.value.trim() !== "") {
					this.#Dropdown.Open();
				}
			});
		}

		if (this.closest("chip-dropdown")) {
			input.onchange = ev => {
				ev.preventDefault();
				ev.stopImmediatePropagation();
				ev.stopPropagation();
			}
		}

		if (this.suffixIcon) { input.classList.add("border-right-none", "br-right-none"); }
		if (this.type === "date") { input.addEventListener("keydown", this.#HandleDateKeydown); }

		if (this.prefixButton) {
			const prefixDropdown = this.querySelector("chip-prefixdropdown"),
				pfxButton = document.createElementWithContents(!prefixDropdown ? "chip-button" : "chip-dropdownbutton", null, {
					className: "input__bound",
					buttonStyle: "icon",
					icon: this.prefixButton,
					variation: "body",
					onclick: () => this.raiseEvent("prefixclick")
				});

			if (prefixDropdown) {
				pfxButton.appendChild(prefixDropdown.firstElementChild);
				prefixDropdown.remove();
			}

			input.insertAdjacentElement("beforebegin", pfxButton);
		} else if (this.prefixIcon) {
			input.insertAdjacentElement("beforebegin", document.createElementWithContents("span", document.createElementWithContents("i", null, { className: this.prefixIcon }), { className: "input__bound" }));
		}

		if (this.suffixButton) {
			const suffixDropdown = this.querySelector("chip-suffixdropdown"),
				sfxButton = document.createElementWithContents(!suffixDropdown ? "chip-button" : "chip-dropdownbutton", null, {
					className: "input__bound",
					buttonStyle: "icon",
					icon: this.suffixButton,
					variation: "body",
					onclick: () => this.raiseEvent("suffixclick")
				});

			if (suffixDropdown) {
				sfxButton.appendChild(suffixDropdown.firstElementChild);
				suffixDropdown.remove();
			}

			input.insertAdjacentElement("afterend", sfxButton);
		} else if (this.suffixIcon) {
			input.insertAdjacentElement("afterend", document.createElementWithContents("span", document.createElementWithContents("i", null, { className: this.suffixIcon }), { className: "input__bound" }));
		}

		if (this.prefixTooltip) { input.previousElementSibling?.setAttribute("tooltip", this.prefixTooltip); }
		if (this.suffixTooltip) { input.nextElementSibling?.setAttribute("tooltip", this.suffixTooltip); }

		if (!this.label && !this.secondaryLabel) { element.querySelector("label").remove(); }
		if (!this.label && this.#Mappers.placeholder) { input.ariaLabel = this.#Mappers.placeholder; }

		Object.keys(this.#Mappers).filter(x => this.#Mappers[x])
			.forEach(key => input.setAttribute(key, this.#Mappers[key] === true ? "" : this.#Mappers[key]));

		this.prepend(...element.childNodes);
	}

	attributeChanged(name, _, newValue) {
		switch (name) {
			case "value":
			case "placeholder":
			case "type":
				this.querySelector("input")[name] = newValue;
				break;
			case "suffix-button":
				this.querySelector("input+chip-button.input__bound").icon = newValue;
				break;
			case "suffix-tooltip":
				this.querySelector("input+.input__bound")?.setAttribute("tooltip", newValue);
				break;
			case "readonly":
				this.querySelector("input").readOnly = this.IsAttributeValueTrue(newValue);
				break;
			case "helper-text":
				{
					const existing = this.querySelector(".input-field__helper-text");

					if (existing) {
						if (newValue) {
							existing.textContent = newValue;
						} else {
							existing.remove();
						}
					} else {
						this.appendChild(document.createElementWithContents("div", newValue, { className: "input-field__helper-text" }));
					}
				}
				break;
		}
	}

	checkValidity() {
		return this.querySelector("input")?.checkValidity() ?? false;
	}

	setInvalid(message) {
		const input = this.querySelector("input");

		if (!this.disableMessages) {
			this.appendChild(document.createElementWithContents("div", message, { className: "input-field__validation-error" }));
		}

		input.classList.add("input-field__invalid");

		this.querySelectorAll(".input__bound").forEach(x => x.classList.add("input-field__invalid"));
	}

	reportValidity() {
		const input = this.querySelector("input"),
			validity = input.checkValidity();

		this.querySelector(".input-field__validation-error")?.remove();
		this.querySelectorAll(".input-field__invalid").forEach(x => x.removeClass("input-field__invalid"));

		if (!validity) {
			this.setInvalid(input.validationMessage);
		}

		return validity;
	}

	focus() {
		this.querySelector("input")?.focus();
	}

	select() {
		this.querySelector("input")?.select();
	}

	#HandleDateKeydown(ev) {
		let date;
		const { target } = ev;

		if (ev.key?.toUpperCase() === "T") { date = new Date(); }
		else if (ev.key?.toUpperCase() === "Y") { date = new Date(new Date().setDate(new Date().getDate() - 1)); }

		if (date) {
			try {
				target.valueAsDate = date;
			} catch {
				target.value = date.toISOString().slice(0, 19);
			}

			target.dispatchEvent(new Event("input", {
				view: window,
				bubbles: true,
				cancelable: false
			}));
		}
	}
}